import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Lightbulb, Users, Handshake, ShieldCheck, Heart, MapPin, Buildings, ArrowRight } from '@phosphor-icons/react';
import { applyPageSeo } from '../utils/seo';

const values = [
  {
    icon: Heart,
    title: 'Client first',
    text: 'We listen first, then build. Every project starts with the business goal, not a generic template.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure by design',
    text: 'Websites, apps and automation are delivered with security, backups and clean code as the default.'
  },
  {
    icon: Handshake,
    title: 'Long-term partners',
    text: 'We stay after launch — support, updates and growth ideas so your system keeps working.'
  }
];

const steps = [
  { step: '01', title: 'Discover', text: 'Understand your process, customers and what success looks like.' },
  { step: '02', title: 'Design', text: 'Plan the product, UI and workflow before a single line of production code.' },
  { step: '03', title: 'Build', text: 'Develop, test and launch on a timeline you can actually track.' },
  { step: '04', title: 'Grow', text: 'Support, SEO, WhatsApp, IVR and upgrades as the business scales.' }
];

const founders = [
  {
    name: 'Vivek Kalaskar',
    role: 'Founder',
    education: 'B.Tech – Computer Science Engineering',
    description: 'Leads product, software and digital delivery. Focused on practical systems that help local and national businesses grow.',
    image: '/logo21.jpeg'
  },
  {
    name: 'Sarthak Shah',
    role: 'Founder',
    education: 'B.Tech – Computer Science Engineering',
    description: 'Leads quality, scalability and client partnerships. Builds reliable technology with clear communication.',
    image: '/logo22.png'
  }
];

const About = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    return applyPageSeo({
      title: 'About Us | Growmore IT Services Satara',
      description:
        'Growmore IT Services is a software and IT solutions company in Satara, Maharashtra. We build websites, apps, ERP, WhatsApp API and digital marketing for India.',
      keywords:
        'about Growmore IT Services, IT company Satara, software company Satara Maharashtra',
      path: '/about'
    });
  }, []);

  return (
    <div className="relative pt-24 pb-12 sm:pt-28 sm:pb-20 min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
      <div className="pointer-events-none absolute -top-24 -left-16 w-80 h-80 rounded-full bg-brand-200/40 blur-3xl animate-pulse" />
      <div className="pointer-events-none absolute top-40 -right-20 w-96 h-96 rounded-full bg-indigo-200/30 blur-3xl" />

      <div className="section-max-width px-4 sm:px-6 lg:px-8 relative">
        <header className="max-w-3xl mx-auto text-center mb-8 sm:mb-14 intern-card-enter">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-5">
            <Buildings className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600 dark:text-brand-400" weight="fill" />
            <span className="text-[11px] sm:text-xs font-semibold tracking-[0.16em] uppercase">Our Company</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-[1.2] mb-3 sm:mb-4">
            About <span className="text-gradient">Growmore IT Services</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-6 sm:leading-7 max-w-2xl mx-auto">
            A Satara-based IT company founded in 2023. We help businesses go digital with custom software, websites and automation — without the agency noise.
          </p>
        </header>

        <section className="mb-14 intern-card-enter">
          <article className="rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-5 sm:p-10 shadow-soft">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white flex items-center justify-center shadow-md shrink-0">
                <MapPin className="w-5 h-5" weight="fill" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold text-gray-900 dark:text-white leading-none">Our story</h2>
                <p className="text-sm font-semibold text-brand-600 mt-1.5">Satara, Maharashtra</p>
              </div>
            </div>
            <div className="space-y-4 text-base text-gray-600 dark:text-gray-300 leading-7">
              <p>
                Growmore IT Services was founded in 2023 in Godoli, Satara. The idea was simple: local businesses deserve the same quality of technology as large metros — websites that load fast, software that matches real workflows, and support that actually picks up the phone.
              </p>
              <p>
                From this headquarters we deliver software, web and automation for government offices, manufacturers, service brands and growing startups across Maharashtra and India. The team stays close to the client from first call to go-live.
              </p>
            </div>
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400 leading-6 border-t border-gray-100 dark:border-gray-800 pt-5">
              Founded in 2023 · Headquarters in Godoli, Satara · Focused on software, web and automation.
            </p>
          </article>
        </section>

        <section className="grid md:grid-cols-2 gap-6 mb-16 items-stretch">
          <article className="intern-card-enter group about-step rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-8 hover:shadow-medium hover:-translate-y-1 hover:border-brand-200 smooth-transition h-full" style={{ animationDelay: '80ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="about-step-mark w-11 h-11 rounded-2xl bg-brand-50 dark:bg-brand-900/30 text-brand-600 flex items-center justify-center shrink-0 smooth-transition">
                <Target className="w-6 h-6" weight="duotone" />
              </div>
              <h2 className="font-sans text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Mission</h2>
            </div>
            <p className="font-sans text-base text-gray-600 dark:text-gray-300 leading-7">
              Give every business a clear, secure digital system — so they can sell more, serve faster and spend less time on manual work.
            </p>
          </article>
          <article className="intern-card-enter group about-step rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-8 hover:shadow-medium hover:-translate-y-1 hover:border-brand-200 smooth-transition h-full" style={{ animationDelay: '140ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="about-step-mark w-11 h-11 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-600 flex items-center justify-center shrink-0 smooth-transition">
                <Lightbulb className="w-6 h-6" weight="duotone" />
              </div>
              <h2 className="font-sans text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Vision</h2>
            </div>
            <p className="font-sans text-base text-gray-600 dark:text-gray-300 leading-7">
              Be the most trusted technology partner from Satara — known for honest delivery, clean products and relationships that last.
            </p>
          </article>
        </section>

        <section className="mb-16">
          <h2 className="font-sans text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white text-center mb-8 intern-card-enter">
            How we <span className="text-gradient">work</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((item, index) => (
              <article
                key={item.step}
                className="about-step intern-card-enter relative rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-6 h-full hover:-translate-y-1 hover:shadow-medium hover:border-brand-200 smooth-transition"
                style={{ animationDelay: `${100 + index * 70}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <p className="about-step-mark inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white font-sans font-extrabold text-sm shadow-sm shrink-0">
                    {item.step}
                  </p>
                  <h3 className="font-sans text-xl font-bold text-gray-900 dark:text-white">{item.title}</h3>
                </div>
                <p className="font-sans text-base text-gray-600 dark:text-gray-300 leading-7">{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-sans text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white text-center mb-8 intern-card-enter">
            What we <span className="text-gradient">stand for</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {values.map((item, index) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.title}
                  className="about-step intern-card-enter rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-6 h-full hover:-translate-y-1 hover:shadow-medium hover:border-brand-200 smooth-transition"
                  style={{ animationDelay: `${120 + index * 80}ms` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="about-step-mark w-11 h-11 rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6" weight="duotone" />
                    </div>
                    <h3 className="font-sans text-xl font-bold text-gray-900 dark:text-white">{item.title}</h3>
                  </div>
                  <p className="font-sans text-base text-gray-600 dark:text-gray-300 leading-7">{item.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section>
          <div className="text-center mb-10 intern-card-enter">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full badge-primary mb-4">
              <Users className="w-5 h-5 text-brand-600" weight="fill" />
              <span className="text-xs font-semibold tracking-[0.16em] uppercase">Leadership</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
              Meet our <span className="text-gradient">founders</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            {founders.map((founder, index) => (
              <article
                key={founder.name}
                className="founder-card intern-card-enter bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-gray-800 p-8 text-center h-full flex flex-col items-center smooth-transition"
                style={{ animationDelay: `${160 + index * 120}ms` }}
              >
                <img src={founder.image} alt={founder.name} className="w-28 h-28 rounded-full object-cover object-top mb-5 border-4 border-white shadow-md ring-2 ring-brand-100" />
                <h3 className="font-display text-xl font-bold text-gray-900 dark:text-white">{founder.name}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-600 mt-1.5 mb-2">{founder.role}</p>
                <p className="text-sm text-gray-500 mb-3">{founder.education}</p>
                <p className="text-base text-gray-600 dark:text-gray-300 leading-7 flex-1">{founder.description}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="mt-16 text-center intern-card-enter">
          <Link to="/contact" className="btn-primary text-white inline-flex items-center gap-2">
            Talk to our team
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
